import { useEffect, useMemo } from 'react';
import intersectionBy from 'lodash/intersectionBy';
import differenceWith from 'lodash/differenceWith';

import { usePersistentState } from '@utils/storage';
import { ignoredUnpaidOrdersKey } from '@constants';

import { useUnpaidOrdersNotifyQuery } from './graphql.v4.generated';

export const useUnpaidOrdersList = () => {
  const [ignoredIds, setIgnoredIds] = usePersistentState<number[]>(ignoredUnpaidOrdersKey, []);
  const { data, loading } = useUnpaidOrdersNotifyQuery({
    fetchPolicy: 'network-only',
    context: {
      v4: true,
      batch: true,
      ignoreAuthError: true,
    },
    ssr: false,
  });
  const unpaidOrders = data?.viewer?.purchases?.items;
  const notIgnoredOrders = useMemo(
    () => (
      unpaidOrders
        ? differenceWith(unpaidOrders, ignoredIds || [], (a, b) => a.id === b)
        : []
    ),
    [unpaidOrders, ignoredIds],
  );

  // Keep ignored ids fresh
  // remove orders which are not unpaid
  useEffect(
    () => {
      if (unpaidOrders) {
        setIgnoredIds(
          intersectionBy(
            ignoredIds,
            unpaidOrders.map((order) => order.id),
          ),
        );
      }
    },
    [unpaidOrders, setIgnoredIds, ignoredIds],
  );

  return {
    loading,
    orders: notIgnoredOrders,
    ignore: (orderId: number) => (
      ignoredIds!.includes(orderId)
        ? undefined
        : setIgnoredIds([...(ignoredIds || []), orderId])
    ),
  };
};
